import React from 'react';
import { cn } from '@/lib/utils';
import { Heart, Coins, Trash2 } from 'lucide-react';

export const ChatBubble = ({ message, onLike, onDonate, onDelete, currentUser }) => {
  const { sender_name, content, message_type, timestamp, is_ai, photo_url, likes = [], drunk_coins = 0 } = message;
  
  const formatTime = (ts) => {
    try {
      return new Date(ts).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '';
    }
  };

  const isLiked = likes.includes(currentUser);
  const canInteract = !is_ai && sender_name !== currentUser;
  const isOwnMessage = sender_name === currentUser;

  if (message_type === 'system') {
    return (
      <div className="flex justify-center my-4 slide-up" data-testid="chat-bubble-system">
        <div className="message-bubble message-system">
          <p className="font-body text-base">{content}</p>
        </div>
      </div>
    );
  }

  // Photo message
  if (message_type === 'photo' && photo_url) {
    return (
      <div 
        className="flex flex-col items-end mb-4 slide-up"
        data-testid="chat-bubble-photo"
      >
        <div className="message-bubble message-user max-w-[85%]">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-heading font-bold text-sm">
              📸 {sender_name}
            </span>
            <span className="text-xs opacity-60 font-mono">
              {formatTime(timestamp)}
            </span>
          </div>
          <img 
            src={photo_url} 
            alt={content || "Party photo"} 
            className="rounded-lg border-2 border-black max-w-full max-h-64 object-cover mb-2"
          />
          {content && content !== "📸 Photo added to the party!" && (
            <p className="font-body text-sm italic">&ldquo;{content}&rdquo;</p>
          )}
          
          {/* Interaction buttons for photos */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-black/20">
            <button
              onClick={() => onLike && onLike(message.id)}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold transition-all",
                isLiked ? "bg-pink text-white" : "bg-white/50 hover:bg-white"
              )}
              data-testid={`like-btn-${message.id}`}
            >
              <Heart className={cn("w-4 h-4", isLiked && "fill-current")} />
              {likes.length > 0 && likes.length}
            </button>
            
            {canInteract && (
              <button
                onClick={() => onDonate && onDonate(message.id, sender_name)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold bg-yellow hover:bg-yellow/80 transition-all"
                data-testid={`donate-btn-${message.id}`}
              >
                <Coins className="w-4 h-4" />
                Tip {drunk_coins > 0 && `(${drunk_coins})`}
              </button>
            )}
            
            {isOwnMessage && (
              <button
                onClick={() => onDelete && onDelete(message.id)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold bg-red-100 hover:bg-red-200 text-red-600 transition-all ml-auto"
                data-testid={`delete-btn-${message.id}`}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <span className="text-xs bg-pink text-white px-2 py-1 rounded-full border border-black mt-1 font-bold">
          📷 PARTY PIC
        </span>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "flex flex-col mb-4 slide-up",
        is_ai ? "items-start" : "items-end"
      )}
      data-testid={`chat-bubble-${is_ai ? 'ai' : 'user'}`}
    >
      <div className={cn(
        "message-bubble",
        is_ai ? "message-ai" : "message-user"
      )}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-heading font-bold text-sm">
            {is_ai ? `🤖 ${sender_name}` : `🍺 ${sender_name}`}
          </span>
          <span className="text-xs opacity-60 font-mono">
            {formatTime(timestamp)}
          </span>
          {drunk_coins > 0 && (
            <span className="text-xs bg-yellow px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              🪙 {drunk_coins}
            </span>
          )}
        </div>
        <p className="font-body text-base leading-relaxed whitespace-pre-wrap">{content}</p>
        
        {/* Interaction buttons - only for non-AI messages */}
        {!is_ai && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-black/20">
            <button
              onClick={() => onLike && onLike(message.id)}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold transition-all",
                isLiked ? "bg-pink text-white" : "bg-white/50 hover:bg-white"
              )}
              data-testid={`like-btn-${message.id}`}
            >
              <Heart className={cn("w-4 h-4", isLiked && "fill-current")} />
              {likes.length > 0 && likes.length}
            </button>
            
            {canInteract && (
              <button
                onClick={() => onDonate && onDonate(message.id, sender_name)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold bg-yellow hover:bg-yellow/80 transition-all"
                data-testid={`donate-btn-${message.id}`}
              >
                <Coins className="w-4 h-4" />
                Tip {drunk_coins > 0 && `(${drunk_coins})`}
              </button>
            )}
            
            {isOwnMessage && (
              <button
                onClick={() => onDelete && onDelete(message.id)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold bg-red-100 hover:bg-red-200 text-red-600 transition-all ml-auto"
                data-testid={`delete-btn-${message.id}`}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {message_type === 'idea' && !is_ai && (
        <span className="text-xs bg-yellow text-black px-2 py-1 rounded-full border border-black mt-1 font-bold">
          💡 IDEA
        </span>
      )}
    </div>
  );
};

export const TypingIndicator = ({ aiName = "Drunk AI" }) => (
  <div className="flex items-start mb-4" data-testid="typing-indicator">
    <div className="message-bubble message-ai">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-heading font-bold text-sm">🤖 {aiName}</span>
      </div>
      <div className="flex gap-1">
        <span className="typing-dot w-2 h-2 bg-black rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-black rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-black rounded-full"></span>
      </div>
    </div>
  </div>
);

export default ChatBubble;
